import { Fragment, forwardRef, Ref } from 'react';
import { RadioGroup } from '@headlessui/react';
import { cn } from '../../../utils';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { RadioCardOptionProps, OrientationProp } from './RadioSmallCards';
import ErrorOrHelp from '../ErrorOrHelp';
import { Prettify } from '@types';

type RadioLargeCardOption = Prettify<
    RadioCardOptionProps & {
        title: string;
        description: string;
        stat: string;
    }
>;

export type RadioLargeCardsProps = {
    title?: string;
    onChange?: (value: string) => void;
    options: RadioLargeCardOption[];
    error?: string;
    name: string;
    value?: string;
} & OrientationProp;

export default forwardRef(function RadioLargeCards(
    {
        title,
        onChange,
        orientation = 'vertical',
        options,
        error,
        name,
        value,
        ...rest
    }: RadioLargeCardsProps,
    ref: Ref<HTMLElement>,
) {
    return (
        <RadioGroup
            {...rest}
            name={name}
            ref={ref}
            value={value}
            onChange={onChange}>
            {title && (
                <RadioGroup.Label className="alli-text-base alli-font-semibold alli-leading-6 alli-text-blue-gray-700">
                    {title}
                </RadioGroup.Label>
            )}

            <div
                className={cn('alli-mt-4', {
                    'alli-flex alli-flex-col alli-space-y-5':
                        orientation === 'vertical',
                    'alli-flex alli-flex-wrap alli-gap-5':
                        orientation !== 'vertical',
                })}>
                {options.map(option => (
                    <RadioGroup.Option
                        key={option.id}
                        value={option.value}
                        data-testid={`radio-large-card-${option.id}`}
                        disabled={option.disabled}
                        className={({ active, disabled }) =>
                            cn(
                                'alli-relative alli-flex alli-w-72 alli-cursor-pointer alli-rounded-lg alli-border alli-bg-white alli-p-4 alli-shadow-sm focus:alli-outline-none',
                                {
                                    'alli-cursor-not-allowed alli-border alli-border-gray-500 alli-opacity-50':
                                        disabled,
                                    ' alli-border-transparent hover:alli-bg-blue-gray-50 focus:alli-border-blue-600 focus:alli-ring-1 focus:alli-ring-blue-600':
                                        active,
                                    'alli-border-gray-500 hover:alli-bg-blue-gray-50':
                                        !active && !disabled,
                                    'alli-border-red-400': error && !disabled,
                                },
                            )
                        }>
                        {({ checked, active }) => (
                            <Fragment key={option.id}>
                                <span className="alli-flex alli-flex-1">
                                    <span className="alli-flex alli-flex-col">
                                        <RadioGroup.Label
                                            as="span"
                                            className="alli-block alli-text-sm alli-font-medium alli-text-blue-gray-700">
                                            {option.title}
                                        </RadioGroup.Label>
                                        <RadioGroup.Description
                                            as="span"
                                            className="alli-mt-1 alli-flex alli-items-center alli-text-sm alli-text-gray-700">
                                            {option.description}
                                        </RadioGroup.Description>
                                        <RadioGroup.Description
                                            as="span"
                                            className="alli-mt-6 alli-text-sm alli-font-medium alli-text-blue-gray-700">
                                            {option.stat}
                                        </RadioGroup.Description>
                                    </span>
                                </span>
                                <CheckCircleIcon
                                    className={cn(
                                        {
                                            'alli-invisible': !checked,
                                        },
                                        'alli-h-5 alli-w-5 alli-text-blue-600',
                                    )}
                                    aria-hidden="true"
                                />
                                <span
                                    className={cn(
                                        active ? 'alli-border' : 'alli-border',
                                        checked
                                            ? 'alli-border-blue-600'
                                            : 'alli-border-transparent',
                                        'alli-pointer-events-none alli-absolute -alli-inset-px alli-rounded-lg',
                                    )}
                                    aria-hidden="true"
                                />
                            </Fragment>
                        )}
                    </RadioGroup.Option>
                ))}
            </div>
            {error && <ErrorOrHelp error={error} name={name} />}
        </RadioGroup>
    );
});
